import { useState, useRef, useEffect, useLayoutEffect, useCallback, useMemo, useId } from 'react';
import { createPortal } from 'react-dom';

import { fromDateString } from '@constants/semester';
import {
  buildClassName,
  computeFloatingPanelPosition,
  DEFAULT_PANEL_HEIGHT,
  DEFAULT_PANEL_WIDTH,
} from '@utils';
import type { FloatingPanelPosition } from '@utils';

import './ThursdayDatePicker.css';

/** Calendar icon SVG - uses currentColor so trigger's design-token color applies in light/dark. */
function CalendarIcon({ size = 24 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  );
}

export interface ThursdayDatePickerProps {
  /** Field label */
  label: string;
  /** Current value YYYY-MM-DD */
  value: string;
  /** Called when user selects a Thursday */
  onChange: (value: string) => void;
  /** Exactly the Thursdays that can be selected (YYYY-MM-DD). Use activeSemester.thursdays. */
  selectableDates: string[];
  /** Thursdays that already have saved attendance (shown with a recorded indicator in the calendar). */
  recordedDates?: string[];
  /** Whether the field is required */
  required?: boolean;
  /** Full width of container */
  fullWidth?: boolean;
  /** Input name for form submission */
  name?: string;
  /** Id for label association */
  id?: string;
  /** Called when the field loses focus (e.g. for validation-on-blur) */
  onBlur?: () => void;
  /** Called when the field receives focus */
  onFocus?: () => void;
}

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];
const DAYS_HEADER = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

export function ThursdayDatePicker({
  label,
  value,
  onChange,
  selectableDates,
  recordedDates,
  required = false,
  fullWidth = false,
  name,
  id: idProp,
  onBlur,
  onFocus,
}: ThursdayDatePickerProps) {
  /** Exactly the 12 allowed dates (YYYY-MM-DD). Stable so calendar logic never drifts. */
  const allowedSet = useMemo(
    () => new Set(selectableDates?.length ? selectableDates : []),
    [selectableDates]
  );
  const recordedSet = useMemo(
    () => new Set(recordedDates?.length ? recordedDates : []),
    [recordedDates]
  );

  const [open, setOpen] = useState(false);
  const [viewDate, setViewDate] = useState(() => {
    const parsed = fromDateString(value);
    if (parsed) return parsed;
    const first = selectableDates?.[0];
    if (first) {
      const d = fromDateString(first);
      if (d) return d;
    }
    return new Date();
  });
  const containerRef = useRef<HTMLDivElement>(null);
  const inputWrapRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [dropdownStyle, setDropdownStyle] = useState<FloatingPanelPosition | null>(null);

  const generatedId = useId();
  const id = idProp ?? `bhg-thursday-picker-${generatedId}`;

  // Show the month of a newly selected/controlled value.
  const [prevValue, setPrevValue] = useState(value);
  if (value !== prevValue) {
    setPrevValue(value);
    const parsed = fromDateString(value);
    if (parsed) setViewDate(new Date(parsed));
  }

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      const insidePicker = containerRef.current?.contains(target);
      const insideDropdown = dropdownRef.current?.contains(target);
      if (!insidePicker && !insideDropdown) {
        setOpen(false);
      }
    };
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [open]);

  const updateDropdownPosition = useCallback(() => {
    if (!inputWrapRef.current) return;
    const rect = inputWrapRef.current.getBoundingClientRect();
    const height = dropdownRef.current?.offsetHeight ?? DEFAULT_PANEL_HEIGHT;
    const width = dropdownRef.current?.offsetWidth ?? DEFAULT_PANEL_WIDTH;
    setDropdownStyle(computeFloatingPanelPosition(rect, height, width));
  }, []);

  useLayoutEffect(() => {
    if (!open || !inputWrapRef.current) {
      setDropdownStyle(null);
      return;
    }
    updateDropdownPosition();
    const raf = requestAnimationFrame(updateDropdownPosition);
    window.addEventListener('scroll', updateDropdownPosition, true);
    window.addEventListener('resize', updateDropdownPosition);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('scroll', updateDropdownPosition, true);
      window.removeEventListener('resize', updateDropdownPosition);
    };
  }, [open, updateDropdownPosition, viewDate]);

  const displayValue = value
    ? (() => {
        const d = fromDateString(value);
        return d ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';
      })()
    : '';

  const yearMonth = viewDate.getFullYear();
  const monthIndex = viewDate.getMonth();
  const firstOfMonth = new Date(yearMonth, monthIndex, 1);
  const startPad = firstOfMonth.getDay();
  const daysInMonth = new Date(yearMonth, monthIndex + 1, 0).getDate();
  const weeks: (number | null)[][] = [];
  let week: (number | null)[] = [];
  for (let i = 0; i < startPad; i++) week.push(null);
  for (let d = 1; d <= daysInMonth; d++) {
    week.push(d);
    if (week.length === 7) {
      weeks.push(week);
      week = [];
    }
  }
  if (week.length) {
    while (week.length < 7) week.push(null);
    weeks.push(week);
  }

  /** Build YYYY-MM-DD from the calendar cell (yearMonth, monthIndex, day) — no Date, no timezone drift. */
  const cellToKey = (day: number) =>
    `${yearMonth}-${String(monthIndex + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

  const handleSelect = (day: number) => {
    const key = cellToKey(day);
    if (!allowedSet.has(key)) return;
    onChange(key);
    setOpen(false);
  };

  const prevMonth = () => setViewDate(new Date(yearMonth, monthIndex - 1));
  const nextMonth = () => setViewDate(new Date(yearMonth, monthIndex + 1));

  const rootClasses = buildClassName(
    'bhg-thursday-picker',
    fullWidth && 'bhg-thursday-picker--full-width',
    open && 'bhg-thursday-picker--open'
  );

  return (
    <div className={rootClasses} ref={containerRef}>
      <label htmlFor={id} className="bhg-thursday-picker__label">
        <span className="bhg-thursday-picker__label-text">
          {label}
          {required && (
            <span className="bhg-thursday-picker__required" aria-hidden="true">
              {' '}
              *
            </span>
          )}
        </span>
      </label>
      <div className="bhg-thursday-picker__input-wrap" ref={inputWrapRef}>
        <input
          id={id}
          type="text"
          role="combobox"
          aria-controls={`${id}-calendar`}
          value={displayValue}
          readOnly
          placeholder="Select a Thursday"
          className="bhg-thursday-picker__input"
          onClick={() => {
            if (open) setOpen(false);
            else {
              if (value) {
                const d = fromDateString(value);
                if (d) setViewDate(d);
              } else if (selectableDates?.[0]) {
                const d = fromDateString(selectableDates[0]);
                if (d) setViewDate(d);
              }
              setOpen(true);
            }
          }}
          onFocus={() => {
            if (!open) {
              if (value) {
                const d = fromDateString(value);
                if (d) setViewDate(d);
              } else if (selectableDates?.[0]) {
                const d = fromDateString(selectableDates[0]);
                if (d) setViewDate(d);
              }
              setOpen(true);
            }
            onFocus?.();
          }}
          onBlur={onBlur}
          aria-haspopup="dialog"
          aria-expanded={open}
          aria-label={label}
        />
        {name && (
          <input type="hidden" name={name} value={value} />
        )}
        <button
          type="button"
          className="bhg-thursday-picker__trigger"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (open) setOpen(false);
            else {
              if (value) {
                const d = fromDateString(value);
                if (d) setViewDate(d);
              } else if (selectableDates?.[0]) {
                const d = fromDateString(selectableDates[0]);
                if (d) setViewDate(d);
              }
              setOpen(true);
            }
          }}
          aria-label="Open calendar"
        >
          <CalendarIcon size={24} />
        </button>
      </div>
      {open &&
        createPortal(
          <div
            ref={dropdownRef}
            className={buildClassName(
              'bhg-thursday-picker__dropdown',
              'bhg-thursday-picker__dropdown--portal',
              recordedDates !== undefined && 'bhg-thursday-picker__dropdown--attendance-status',
              dropdownStyle?.placement === 'above' && 'bhg-thursday-picker__dropdown--above'
            )}
            id={`${id}-calendar`}
            role="dialog"
            aria-label="Choose a Thursday"
            style={{
              position: 'fixed',
              top: dropdownStyle?.top ?? 0,
              left: dropdownStyle?.left ?? 0,
              visibility: dropdownStyle ? 'visible' : 'hidden',
            }}
          >
          <div className="bhg-thursday-picker__header">
            <button
              type="button"
              className="bhg-thursday-picker__nav"
              onClick={prevMonth}
              aria-label="Previous month"
            >
              ‹
            </button>
            <span className="bhg-thursday-picker__month">
              {MONTHS[monthIndex]} {yearMonth}
            </span>
            <button
              type="button"
              className="bhg-thursday-picker__nav"
              onClick={nextMonth}
              aria-label="Next month"
            >
              ›
            </button>
          </div>
          <div className="bhg-thursday-picker__days-header">
            {DAYS_HEADER.map((d, i) => (
              <span key={i} className="bhg-thursday-picker__day-name">
                {d}
              </span>
            ))}
          </div>
          <div className="bhg-thursday-picker__grid">
            {weeks.map((row, ri) =>
              row.map((day, di) => {
                if (day === null) {
                  return <span key={`${ri}-${di}`} className="bhg-thursday-picker__cell bhg-thursday-picker__cell--empty" />;
                }
                const dateKey = cellToKey(day);
                const isAllowed = allowedSet.has(dateKey);
                const selected = value === dateKey;
                const showAttendanceStatus = recordedDates !== undefined && isAllowed;
                const isRecorded = isAllowed && recordedSet.has(dateKey);
                return (
                  <button
                    key={`${ri}-${di}-${day}`}
                    type="button"
                    className={buildClassName(
                      'bhg-thursday-picker__cell',
                      isAllowed && 'bhg-thursday-picker__cell--selectable',
                      !isAllowed && 'bhg-thursday-picker__cell--disabled',
                      selected && 'bhg-thursday-picker__cell--selected'
                    )}
                    disabled={!isAllowed}
                    onClick={() => handleSelect(day)}
                    {...(showAttendanceStatus
                      ? { 'data-attendance': isRecorded ? 'recorded' : 'pending' }
                      : {})}
                    aria-label={
                      showAttendanceStatus
                        ? `${day}${isRecorded ? ', attendance taken' : ', attendance not taken yet'}`
                        : undefined
                    }
                  >
                    {day}
                  </button>
                );
              })
            )}
          </div>
          <p className="bhg-thursday-picker__hint">
            {recordedDates !== undefined
              ? 'Green = attendance taken. Amber outline = not taken yet. Includes the make-up day when set.'
              : 'Only Thursdays during the current semester are available (including the make-up day when set).'}
          </p>
        </div>,
          document.body
        )}
    </div>
  );
}
